import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';

/**
 * DataCacheContext - Manages caching of ticker data to reduce Supabase API calls
 * 
 * Features:
 * - In-memory cache for instant access during session
 * - Optional IndexedDB persistence for cross-session caching
 * - Indefinite cache duration (no expiry during session)
 * - Manual refresh capability
 */

const DataCacheContext = createContext();

// IndexedDB setup for persistent caching
const DB_NAME = 'DATboardCache';
const STORE_NAME = 'tickerData';
const DB_VERSION = 1;

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'ticker' });
      }
    };
  });
};

const saveToIndexedDB = async (ticker, data) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    await store.put({
      ticker,
      data,
      timestamp: Date.now()
    });
  } catch (error) {
    // IndexedDB save failed
  }
};

export const DataCacheProvider = ({ children }) => {
  // In-memory cache (fast access)
  const [cache, setCache] = useState({});
  const [cacheStats, setCacheStats] = useState({
    hits: 0,
    misses: 0,
    totalSaved: 0
  });

  // Initialize cache from IndexedDB on mount
  useEffect(() => {
    const initCache = async () => {
      try {
        const db = await openDB();
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();
        
        request.onsuccess = () => {
          const items = request.result;
          const initialCache = {};
          
          items.forEach(item => {
            initialCache[item.ticker] = {
              data: item.data,
              timestamp: item.timestamp,
              source: 'indexedDB'
            };
          });
          
          setCache(initialCache);
        };
      } catch (error) {
        // Failed to initialize cache from IndexedDB
      }
    };
    
    initCache();
  }, []);

  /**
   * Get cached data for a ticker
   * @param {string} ticker - Ticker symbol
   * @returns {Object|null} - Cached data or null if not found
   */
  const getCachedData = useCallback((ticker) => {
    if (cache[ticker]) {
      setCacheStats(prev => ({
        ...prev,
        hits: prev.hits + 1
      }));
      
      return cache[ticker].data;
    }
    
    setCacheStats(prev => ({
      ...prev,
      misses: prev.misses + 1
    }));
    
    return null;
  }, [cache]);

  /**
   * Store data in cache (both memory and IndexedDB)
   * @param {string} ticker - Ticker symbol
   * @param {Object} data - Data to cache
   */
  const setCachedData = useCallback((ticker, data) => {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      source: 'api'
    };
    
    // Update in-memory cache
    setCache(prev => ({
      ...prev,
      [ticker]: cacheEntry
    }));
    
    // Persist to IndexedDB (async, non-blocking)
    saveToIndexedDB(ticker, data);
    
    setCacheStats(prev => ({
      ...prev,
      totalSaved: prev.totalSaved + 1
    }));
  }, []);

  /**
   * Check if ticker data exists in cache
   * @param {string} ticker - Ticker symbol
   * @returns {boolean}
   */
  const hasCachedData = useCallback((ticker) => {
    return !!cache[ticker];
  }, [cache]);

  /**
   * Clear cache for specific ticker
   * @param {string} ticker - Ticker symbol
   */
  const clearTickerCache = useCallback((ticker) => {
    setCache(prev => {
      const newCache = { ...prev };
      delete newCache[ticker];
      return newCache;
    });
    
    // Also clear from IndexedDB
    openDB().then(db => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.delete(ticker);
    }).catch(() => {});
  }, []);

  /**
   * Clear all cache
   */
  const clearAllCache = useCallback(() => {
    setCache({});
    setCacheStats({ hits: 0, misses: 0, totalSaved: 0 });
    
    // Clear IndexedDB
    openDB().then(db => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.clear();
    }).catch(() => {});
  }, []);

  /**
   * Get cache statistics
   * @returns {Object} - Cache hit rate and stats
   */
  const getCacheStats = useCallback(() => {
    const total = cacheStats.hits + cacheStats.misses;
    const hitRate = total > 0 ? ((cacheStats.hits / total) * 100).toFixed(1) : 0;
    
    return {
      ...cacheStats,
      hitRate: `${hitRate}%`,
      cachedTickers: Object.keys(cache).length
    };
  }, [cacheStats, cache]);

  const value = useMemo(() => ({
    getCachedData,
    setCachedData,
    hasCachedData,
    clearTickerCache,
    clearAllCache,
    getCacheStats,
    cacheStats
  }), [
    getCachedData,
    setCachedData,
    hasCachedData,
    clearTickerCache,
    clearAllCache,
    getCacheStats,
    cacheStats
  ]);

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
};

/**
 * Hook to use the data cache
 */
export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
};

export default DataCacheContext;


